import _ from 'underscore'
import Backbone from 'backbone'
import Logger from 'Logger'
import { $ajax, $number, mallClose } from '../../utils'
import {
  validateGift,
  validatePokerClass,
  validateBeforeBuyPackage,
} from './pay.validate'

import { requestFriendGsnInfo, requestFriendIdInfo } from './pay.requestFriend'
import {
  API_PATH,
  REFUND_INFO_MONTHLY,
  REFUND_INFO,
  GOODS_ID,
  isPointEnabled,
} from '../../config'
import { itemGuard } from './guard/itemGuard'
import { giftGuard } from './guard/giftGuard'

/**
 * @typedef {Object} PayModel
 * @property {boolean} isVisible - 팝업 온 오프
 * @property {'normal'|'gift'|'pokerClass'} template - 결제 템플릿 타입
 * @property {string} goodsName - 상품명
 * @property {number} purchaseRemain - 이번달 남은 결제 금액
 * @property {number|string} myPmangCash - 내 피망 캐쉬 토탈 (콤마 포함)
 * @property {number} rewardMoney - 리워드 머니
 * @property {number|string} pmangBouns - 피망 보너스 (콤마 포함)
 * @property {number} pmangBounsOrigin - 피망 보너스 원본 (숫자)
 * @property {number} pmangBounsRatio - 피망 보너스 비율
 * @property {Array<Object>} goodsList - 상품 리스트
 * @property {Array<number>} goodsIdList - 상품 ID 리스트
 * @property {number} purchaseCount - 구매 수량
 * @property {number|string} goodsTotalPrice - 총 상품 가격 (콤마 포함)
 * @property {number} goodsTotalPriceOrigin - 총 상품 가격 (숫자)
 * @property {number|string} goodsTotalRewardMoney - 총 보상 골드
 * @property {boolean} isGoodsHaveRewardMoney - 보상 골드 존재 여부
 * @property {number|string} remainPmangCash - 남은 피망 캐쉬
 * @property {number|string} requiredPmangCash - 충전 필요 금액
 * @property {'HP'|'CARD'|'KAKAO'|'PCO'|'NOCB'|'BT'} payNowMethod - 결제 방법
 * @property {boolean} isSelectPmangCash - 피망캐쉬 선택 여부
 * @property {boolean} isSelectPayNow - 바로결제 선택 여부
 * @property {boolean} isRequested - 응답 여부
 * @property {boolean} disablePayMethodNow - 바로구매 비활성화 여부
 * @property {boolean} disablePmangCash - 피망캐시 구매 비활성화 여부
 * @property {boolean} disableGoodsCounter - 수량 변경 불가능 여부
 * @property {boolean} useAllPayNowMethods - 모든 결제 수단 사용 여부
 * @property {Function} onConfirm - 확인 콜백
 * @property {Function} onClose - 닫기 콜백
 * @property {boolean} isPolicyChecked - 정책 동의 여부
 * @property {'normal'|'pokerclass'|'hotdeal'} validationName - 검증 타입
 * @property {string} apiPath - API 경로
 * @property {boolean} isChargePmangCash - 피망캐쉬 충전 필요 여부
 * @property {Object} billingUrl - 빌링창 바로구매 URL
 * @property {boolean} hasFriendList - 친구 리스트 존재 여부
 * @property {number|null} friendGSN - 친구 GSN
 * @property {string} friendId - 친구 ID
 * @property {number|null} friendResultCode - 친구 검증 결과 코드
 * @property {boolean} isFriendSearched - 친구 검색 여부
 * @property {string|null} friendNickname - 친구 별명
 * @property {string|null} myNickname - 내 별명
 * @property {number} classPaymentDate - 클래스 결제일
 * @property {string} refundInfo - 환불 안내
 * @property {boolean} isGoodsPackage - 패키지 상품 여부
 * @property {string} goodsPackageEndDate - 패키지 상품 종료일
 * @property {boolean} clickedPurchaseBtn - 구매 버튼 클릭 여부
 * @property {number} pwgPointGoodsPoint - 제휴 포인트 (상품별)
 * @property {string} pwgPointUserUid - 제휴 포인트 연동 UID
 * @property {string} pwgPointUserAccount - 제휴 포인트 연동 계정
 * @property {number} pwgPointUserBalance - 제휴 포인트 보유 포인트
 * @property {boolean} isPointGoods - 제휴 포인트 연동 상품 여부
 * @property {boolean} isPointEnabled - 제휴 포인트 설정 활성화 여부
 * @property {boolean} isPointActivated - 결제 수단에 따른 제휴 포인트 표시 여부
 * @property {number} estimatedPoint - 구매 수량당 지급 받을 포인트
 * @property {Array<Object>} vip_coupon_info - VIP 쿠폰 정보
 * @property {boolean} canUseVipCoupons - VIP 쿠폰 보유 여부
 * @property {boolean} vipCouponsDisabled - VIP 쿠폰 셀렉트박스 비활성화 여부
 * @property {string} vipCouponRewardMoney - VIP 쿠폰 머니
 * @property {string} vipCouponSelectedId - VIP 쿠폰 선택 ID
 */

/** @type Backbone.model<PayModel> */
var PayModel = Backbone.Model.extend({
  defaults: function () {
    return {
      isVisible: false, // 팝업 온 오프
      template: 'normal', // normal | gift | pokerClass
      goodsName: '', // 상품명
      purchaseRemain: 0, // 이번달 남은 결제 금액
      myPmangCash: 0, // 내 피망 캐쉬 토탈
      rewardMoney: 0,
      pmangBouns: 0, // 콤마 포함
      pmangBounsOrigin: 0, // 숫자
      pmangBounsRatio: 0, // 2022년 11월부터 무통장 / 계좌이체 외에는 피망보너스 지급하지 않음
      goodsList: [], // 외부 컬랙션에서 넘어오는 아이템 배열
      goodsIdList: [], // 상품 ID 만 뽑아서 모아 둔다 요청 보낼때 join('_') 해서 보낸다.
      purchaseCount: 1, // 구매수량
      goodsTotalPrice: 0, // 콤마 포함
      goodsTotalPriceOrigin: 0, // 숫자
      goodsTotalRewardMoney: '', // 총 보상 골드
      isGoodsHaveRewardMoney: false,
      remainPmangCash: 0, // 아이템 가격 - 현재 보유 피망캐쉬
      requiredPmangCash: 0, //충전필요 금액
      payNowMethod: 'HP', // 바로 결제 방법 HP|| CARD || KAKAO || PCO: 페이코|| NOCB: 무통장 입금 || BT : 계좌이체
      isSelectPmangCash: true, // 뷰 피망캐쉬 선택
      isSelectPayNow: false, // 뷰 바로결제 선택
      isRequested: false, // 응답여부
      disablePayMethodNow: false, // 바로구매 비활성화 : 5천원 이하의 상품은 피망 캐쉬만 충전 가능
      disablePmangCash: false, // 피망캐시 구매 비활성화 : 2021 모듬세트 구매시에만 적용됨
      disableGoodsCounter: false, // 다중 아이템 선택 후 구매면 수량 변경 불가능
      useAllPayNowMethods: false, // 바로결제 시 모든 결제 수단 사용 여부
      onConfirm: _.noop(),
      onClose: this.hide,
      isPolicyChecked: false,
      validationName: 'normal', // pokerclass || hotdeal || normal
      apiPath: '',
      isChargePmangCash: false, // 피망 캐쉬 - 구매 금액 = 마이너스 되면 충전 후 구매
      billingUrl: {}, // 빌링창 바로구매 URL

      // 친구 플래그
      hasFriendList: false, // 친구 리스트
      friendGSN: null, // 친구 GSN
      friendId: '',
      friendResultCode: null, // 친구 검증 API 내려온 결과 값
      isFriendSearched: false,
      friendNickname: null, // 친구 별명
      myNickname: null, // 내 별명

      // 클래스
      classPaymentDate: 0, // 클래스 결제일
      refundInfo: '',

      // 모듬세트, 실속세트 등
      isGoodsPackage: false,
      goodsPackageEndDate: '',

      // dirty
      clickedPurchaseBtn: false,

      // 2024년 5월 PWG 제휴 포인트
      // API 응답의 제휴 포인트
      // 구매시 1개당 지급 포인트
      pwgPointGoodsPoint: 0,

      // 연동 UID
      pwgPointUserUid: '',

      // 연동 계정
      pwgPointUserAccount: '',

      // 현재 보유 포인트
      pwgPointUserBalance: 0,

      // 제휴 포인트 연동 상품인가?
      isPointGoods: false,

      // 제휴 포인트 설정 활성화?
      isPointEnabled: isPointEnabled,

      // 결제 수단에 따라 제휴 포인트 표시 여부?
      isPointActivated: false,

      // 구매 수량당 지급 받을 포인트
      estimatedPoint: 0,

      // VIP 쿠폰 정보
      vip_coupon_info: [
        // bonus_money_rate: number;
        // coupon_name: string;
        // vip_coupon_id: number;
        // is_selected: boolean <== 셀렉트박스용
      ],

      // VIP 쿠폰을 가지고 있는지?
      canUseVipCoupons: false,

      // VIP 선택 셀렉트박스
      vipCouponsDisabled: true,

      // VIP 쿠폰 머니
      vipCouponRewardMoney: $number(0).text({ tag: true }),

      // VIP 쿠폰 셀렉트 박스
      vipCouponSelectedId: '0',
    }
  },

  initialize: function () {
    _.bindAll(this, 'hide')

    // 결제방식이 바뀔 때마다 피망 보너스 재계산
    this.on(
      'change:isSelectPmangCash change:isSelectPayNow change:payNowMethod change:goodsTotalPriceOrigin',
      this.calculateBonus,
      this
    )

    this.on(
      'change:isSelectPayNow change:payNowMethod change:purchaseCount',
      this.calculatePoint,
      this
    )

    this.on(
      'change:vipCouponSelectedId change:purchaseCount change:payNowMethod',
      this.calculateVipReward,
      this
    )
  },

  // 요청전 데이터 검증은 backbone 기본 기능으로
  validate: function (attr, options) {
    Logger.info('[paymodel] validate')
    var appData = $('.app__initial-state').data()
    var validationName = this.get('template')

    if (this.get('isGoodsPackage'))
      return this.validateBeforeBuyPackage(attr, options, appData)

    if (validationName === 'pokerClass')
      return this.validatePokerClass(attr, options, appData)

    if (validationName === 'gift')
      return this.validateGift(attr, options, appData)
  },

  validateBeforeBuyPackage: validateBeforeBuyPackage,

  validateGift: validateGift,

  validatePokerClass: validatePokerClass,

  getApiPath: function (templateName) {
    switch (templateName) {
      case 'gift':
        return API_PATH.VALITDATE_GIFT
      case 'normal':
        return API_PATH.VALITDATE_GOODS
      case 'pokerClass':
        return API_PATH.VALITDATE_AUTO
      default:
        return null
    }
  },

  show: function (options) {
    Logger.info('Pay Show')

    var firstGoods = options.goodsList[0] || null

    var isMonthlyGoods =
      firstGoods && _.contains(GOODS_ID.MONTHLY, firstGoods.goods_id)

    var isMonthlyGift = isMonthlyGoods && options.template === 'gift'

    var canBuyOnlyOne =
      firstGoods && _.contains(GOODS_ID.ONE_BY_ONE, firstGoods.goods_id)

    var combineOptions = _.extend(options, {
      isVisible: false, // API 벨리데이트를 통과해야 visible 상태를 바꾼다
      apiPath: this.getApiPath(options.template),

      disableGoodsCounter:
        !(options.template === 'normal' && options.goodsList.length === 1) ||
        isMonthlyGoods || // 카운터 표시 여부 (개월권 이거나 다중 상품이면 카운터 off)
        canBuyOnlyOne, // 하나만 살 수 있는 상품

      disablePmangBonus: isMonthlyGift || firstGoods.disablePmangBonus || false, // 아이템 / 핫딜은 피망 보너스를 지급 하지 않는다.
    })

    this.set(combineOptions)
    this.fetch()
  },

  hide: function () {
    Logger.info('Pay hide')
    this.set(this.defaults())
    mallClose()
  },

  checkPolicy: function () {
    var newPolicy = !this.get('isPolicyChecked')
    this.set({ isPolicyChecked: newPolicy })
  },

  setPayPmangCash: function () {
    this.set({
      isSelectPmangCash: true,
      isSelectPayNow: false,
    })
  },

  setPayNow: function () {
    this.set({
      isSelectPmangCash: false,
      isSelectPayNow: true,
    })
  },

  // 실속세트. 세트용 상품. 2023년 6월부터 모듬세트 판매 중지, 실속세트만 사용
  setGoodsPackage: function (goodsId) {
    if (!_.contains(GOODS_ID.GOODS_PACKAGE, goodsId)) return

    var today = new Date()
    var lastDayOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    var goodsPackageEndDate = [
      '(~',
      lastDayOfMonth.getMonth() + 1,
      '/',
      lastDayOfMonth.getDate(),
      ' 23:59:59 까지',
      ')',
    ].join('')

    this.setPayNow()
    this.set({
      goodsPackageEndDate: goodsPackageEndDate,
      isGoodsPackage: true,
      payNowMethod: 'NOCB',
      disablePmangBonus: true,
      disablePmangCash: true,
      disableGoodsCounter: false, // 구매수량 표시
      clickedPurchaseBtn: false,
    })
  },

  // 모델이 바뀔 때마다 피망 보너스 재계산
  calculateBonus: function (/* model */) {
    var pmangBounsOrigin = this.get('pmangBounsOrigin') // 2022년 11월부터 보너스 없음. 이 값은 0
    var totalPrice = this.get('goodsTotalPriceOrigin')
    var payNowMethod = this.get('payNowMethod')
    var isSelectPayNow = this.get('isSelectPayNow')

    /**
     * 무통장 입금(NOCB), 실시간 계좌이체(BT) 2022 시행령 대응으로 피망보너스 요율 변경 50% -> 30%
     * 2022년 11월부터 그외 결제 수단은 피망보너스 제공하지 않음
     */
    var higherBonusRatio = 0.3
    var bonus = '0'

    if (isSelectPayNow && _.contains(['NOCB', 'BT'], payNowMethod)) {
      bonus = $number(totalPrice * higherBonusRatio).comma()
    } else {
      bonus = $number(pmangBounsOrigin).comma() // 기본
    }

    this.set({
      pmangBouns: bonus,
    })
  },

  calculatePoint: function () {
    var payNowMethod = this.get('payNowMethod')
    var purchaseCount = this.get('purchaseCount')
    var isPointGoods = this.get('isPointGoods')
    var isPointMethod = _.contains(['NOCB', 'BT'], payNowMethod)
    var isPointEnabled = this.get('isPointEnabled')
    var goodsPoint = this.get('pwgPointGoodsPoint')
    var isPointActivated = isPointGoods && isPointMethod && isPointEnabled
    var estimatedPoint = isPointActivated ? goodsPoint * purchaseCount : 0

    this.set({
      estimatedPoint: estimatedPoint,
      isPointActivated: isPointActivated,
    })
  },

  /**
   * VIP 전용 쿠폰 머니 계산
   */
  calculateVipReward: function (/* model */) {
    var payNowMethod = this.get('payNowMethod')
    var isPointMethod = _.contains(['NOCB', 'BT'], payNowMethod)

    // 구매 수량이 2개 이상일 경우, 무통장/실시간 계좌이체일 경우 VIP 쿠폰 사용 불가
    if (this.get('purchaseCount') > 1 || isPointMethod) {
      this.set({
        vipCouponsDisabled: true,
        vipCouponSelectedId: '0',
        vipCouponRewardMoney: $number(0).text({ tag: true }),
        vip_coupon_info: this.get('vip_coupon_info').map((v) => {
          v.is_selected = false
          return v
        }),
      })
      return
    }

    // 1개 구매일때만
    var goodsList = this.get('goodsList')
    var goodsInfo =
      _.isArray(goodsList) && goodsList.length === 1 && goodsList[0]
    var rewardMoney = goodsInfo ? goodsInfo.reward_money || 0 : 0
    var selectedId = this.get('vipCouponSelectedId')
    var currentCoupon = this.get('vip_coupon_info').find(
      (c) => String(c.vip_coupon_id) === selectedId
    )
    var ratio = currentCoupon ? currentCoupon.bonus_money_rate / 100 : 0
    var reward = $number(rewardMoney * ratio).comma()
    var rewardTextx = $number(reward).text({ tag: true })

    var nextValue = {
      vipCouponsDisabled: false,
      vipCouponRewardMoney: rewardTextx,
      vip_coupon_info: this.get('vip_coupon_info').map((v) => {
        v.is_selected = String(v.vip_coupon_id) === selectedId
        return v
      }),
    }
    Logger.info('VIP Reward re-calculated:', nextValue)

    this.set(nextValue)
  },

  /**
   * 특정 상품은 피망캐쉬로 결제를 못하게 하고, 바로 결제를 활성화 한다.
   * 2022년 9월 강제퇴장권 상품 추가로 인한 예외 추가
   * @param {number} goodsId
   */
  setPayNowOnly: function (goodsId) {
    if (!_.contains(GOODS_ID.PAYNOW_ONLY, goodsId)) return

    this.setPayNow() // 바로결제 활성화
    this.set({
      disablePmangCash: true, // 피망캐시 탭 비활성화
      disablePmangBonus: true, // 피망보너스 없음
      useAllPayNowMethods: true, // 무통장, 실시간 계좌이체 외의 모든 결제 수단 활성화
    })
  },

  // 구매 버튼을 눌렀다면 clickedPurchaseBtn 상태 전환
  setDirty: function () {
    this.set({ clickedPurchaseBtn: true })
  },

  restoreComma: function (commaNumber) {
    var array = commaNumber.split(',')
    return array.join('')
  },

  // 바로결제 결제방식 선택
  setPayNowMethod: function (payMethod) {
    this.set({
      payNowMethod: payMethod,
    })
  },

  setFriendData: function (options) {
    this.set(options, { validationName: 'gift', validate: true })
  },

  resetFriend: function () {
    this.set({
      friendResultCode: null,
      friendGSN: '',
      friendNickname: '',
      friendId: '',
      isFriendSearched: false,
      billingUrl: null,
    })
  },
  /**
   * @param {Number} num
   * 구매 수량 변경 변경시 마다 수량 값 넘겨서 다시 fetch
   */
  goodsCounter: function (num) {
    var currentCount = this.get('purchaseCount')
    if (currentCount === 1 && num === -1) return
    if (currentCount === 10 && num === 1) return
    var newCountValue = (currentCount += num)

    if (this.get('template') === 'gift') {
      this.resetFriend()
    }

    this.fetch({ conter: newCountValue })
  },

  /**
   * @param {Array} goodsList
   * @return {Calculated}
   * 상품 배열을 받아 상품이름 간단히 보이게 하고 총가격/리워드 계산
   */
  calcResponseGoods: function (goodsList) {
    var totalPrice = 0
    var totalRewardMoney = 0
    var goodsListLength = goodsList.length
    var goodsName = ''

    // 머니 총합
    _.each(goodsList, function (goods) {
      totalPrice += goods.price
      totalRewardMoney += goods.reward_money
      // 단일 상품 여러개구입시 리스트에 따로 전체 갯수가 넘어와 치환 한다.
      if (goods.count > 0) {
        goodsListLength = goods.count
      }
    })
    // 아이템 여려개 선택 했을때 상품명
    if (goodsList && goodsListLength > 1) {
      goodsName =
        goodsList[0].goods_name + ' 외 ' + (goodsListLength - 1) + '종'
    } else if (goodsListLength === 1) {
      goodsName = goodsList[0].goods_name
    }

    return {
      goodsId: goodsList[0] ? goodsList[0].goods_id : '', // 여러개일 경우 처음 상품의 아이디
      goodsName: goodsName || '',
      totalPrice: totalPrice || 0,
      totalRewardMoney: totalRewardMoney || 0,
    }
  },

  joinGoodsId: function (goodsList, purchaseCountNumber) {
    var goodsIdArray = _.pluck(goodsList, 'goods_id')
    var purchaseCount = purchaseCountNumber
    var multiflyGoodsId = []
    for (var index = 0; index < purchaseCount; index++) {
      multiflyGoodsId.push(goodsIdArray)
    }
    goodsIdArray = _.flatten(multiflyGoodsId)
    return goodsIdArray.join('_')
  },

  requestFriendGsnInfo: requestFriendGsnInfo,

  requestFriendIdInfo: requestFriendIdInfo,

  parse: function (data) {
    // data: utils/dummy-data/goods-model.json 참고
    var myPmangCash =
      (data.pmang_cash_info && data.pmang_cash_info.amt_total) || 0
    var calculatedGoodsList = this.calcResponseGoods(
      data.simple_goods_info || [data.simple_goods_class_info]
    )
    var goodsIdArray = _.pluck(data.simple_goods_info, 'goods_id')
    var isMonthlyGoods = _.contains(GOODS_ID.MONTHLY, goodsIdArray[0])
    var remainPmangCash = myPmangCash - calculatedGoodsList.totalPrice
    var billingUrl = data.pmang_cash_url_info
    var disablePayMethodNow = calculatedGoodsList.totalPrice <= 5000
    var today = new Date()

    // 제휴 포인트 정보가 없을 경우 기본값 설정
    var pwgPoint = data.pwg_point_info || {
      goods_point: 0,
      user_uid: '',
      user_account: '',
      user_balance: 0,
    }

    // 모듬세트, 실속세트 상품을 위한 뷰 변경
    this.setGoodsPackage(goodsIdArray[0] || 0)

    // 강제 퇴장권은 바로결제만 가능
    this.setPayNowOnly(goodsIdArray[0] || 0)

    // 비활성화 목록에 따라 구매 방법 선택
    myPmangCash < 1 && !disablePayMethodNow && this.setPayNow()
    disablePayMethodNow && this.setPayPmangCash()

    var parsed = _.extend(data, {
      myPmangCash: $number(myPmangCash).comma(),
      goodsIdList: goodsIdArray,

      goodsTotalPriceOrigin: calculatedGoodsList.totalPrice,
      goodsTotalPrice: $number(calculatedGoodsList.totalPrice).comma(),

      purchaseRemain:
        data.amt_remain <= 100000 ? $number(data.amt_remain).comma() : null,
      goodsTotalRewardMoney: calculatedGoodsList.totalRewardMoney,
      isGoodsHaveRewardMoney: calculatedGoodsList.totalRewardMoney > 0,
      goodsName: calculatedGoodsList.goodsName,
      remainPmangCash: $number(remainPmangCash).comma(),
      requiredPmangCash: $number(Math.abs(remainPmangCash)).comma(),
      isChargePmangCash: remainPmangCash < 0,
      disablePayMethodNow: disablePayMethodNow,
      billingUrl: billingUrl || null,
      hasFriendList: !!data.friend_list,
      classPaymentDate: today.getDate(),
      refundInfo: isMonthlyGoods // 개월권 이면 환불 안내가 다르게 나가야함 청약철회는 구매일로부터 7일 이내에 가능합니다.(단, 사용했을 경우 청약철회 불가)
        ? REFUND_INFO_MONTHLY
        : REFUND_INFO,

      // 제휴 포인트
      pwgPointGoodsPoint: pwgPoint.goods_point,
      pwgPointUserUid: pwgPoint.user_uid,
      pwgPointUserAccount: pwgPoint.user_account,
      pwgPointUserBalance: pwgPoint.user_balance,

      // 제휴 포인트 연동 상품인지?
      isPointGoods: _.contains(GOODS_ID.POINT_GOODS, goodsIdArray[0]),

      // VIP 쿠폰 : 서버 데이터에 없으면 null
      vip_coupon_info: _.isArray(data.vip_coupon_info)
        ? data.vip_coupon_info
        : [],

      // VIP 쿠폰을 사용 가능한지 여부
      canUseVipCoupons:
        _.isArray(data.vip_coupon_info) &&
        data.vip_coupon_info.length > 0 && // 쿠폰 보유
        goodsIdArray.length === 1 && // 상품 1개 구매일때
        !isMonthlyGoods && // 정기결제 상품 아닐 때
        !_.includes(GOODS_ID.VIP_COUPON_EXCLUDED, goodsIdArray[0]) && // VIP 쿠폰 불가 상품 제외
        calculatedGoodsList.totalRewardMoney > 0, // 리워드 머니가 있을때

      vipCouponsDisabled: goodsIdArray.length !== 1, // 상품이 1개일때만 VIP 쿠폰 사용 가능
    })

    return parsed
  },

  sync: function (method, model, options) {
    if (method === 'read') {
      var payModel = this
      var goodsList = this.get('goodsList')
      var apiPath = this.get('apiPath')
      var purchaseCount = options.conter || 1
      var requestGoodsId = this.joinGoodsId(goodsList, purchaseCount)
      var templateName = this.get('template')

      if (!apiPath) throw new Error('API PATH가 없습니다.')
      Logger.info('SYNC: Pay Model', apiPath)
      Logger.info(' options:', options)

      return new Promise(function (resolve, reject) {
        $ajax(apiPath, { goods_id_list: requestGoodsId })
          .then(function (res) {
            var $df = $.Deferred()
            try {
              itemGuard(res, payModel, resolve, reject)
              giftGuard(res, templateName)
              $df.resolve(res)
            } catch (err) {
              Logger.info('err', err)
              $df.reject(err)
            }

            return $df.promise()
          })
          .done(function (res) {
            options.success(
              _.extend(res.result, {
                purchaseCount: purchaseCount,
                isVisible: true,
              })
            )
          })
          .fail(function (error) {
            Logger.info('validation fail', error)
          })
      })
    }
  },
})

export var payModel = new PayModel()
